<!--This is the element which contains multiple SubmissionErrorCards, shows on top of the home dashboard-->
<template>
	<div
		v-if="submissionErrorGroups.length"
		class="mb-3 flex flex-col gap-2 rounded-2xl border-red-100 bg-red-50 px-6 py-5"
	>
		<button
			type="button"
			class="relative flex w-full items-center justify-center"
			:aria-expanded="errorsExpanded"
			@click="errorsExpanded = !errorsExpanded"
		>
			<div class="flex items-center gap-2">
				<ExclamationTriangleIcon class="h-6 w-6 text-red-500" />
				<div class="text-center font-bold">
					{{ t('document.submission.sectionErrorHeading') }}
				</div>
			</div>

			<ChevronDownIcon
				class="absolute right-0 h-5 w-5 transition-transform"
				:class="{ 'rotate-180': errorsExpanded }"
			/>
		</button>

		<TransitionGroup
			v-if="errorsExpanded"
			name="error-cards"
			tag="div"
			class="flex w-full place-content-center"
			:class="{
				'grid grid-cols-1 gap-3 lg:grid-cols-2':
					submissionErrorGroups.length > 1,
			}"
		>
			<SubmissionErrorCard
				v-for="group in submissionErrorGroups"
				:key="group.id"
				:session="group.session"
				:has-only-one-error="submissionErrorGroups.length === 1"
				:plan-holder-id="group.firstPlanHolderId"
				:error-count="group.errorCount"
				@open-submission-status="openSubmissionStatus(group)"
			/>
		</TransitionGroup>
	</div>

	<SubmissionStatusModal
		v-if="statusPlans.length"
		v-model:open="openStatusModal"
		:plans="statusPlans"
	/>
</template>

<script setup lang="ts">
import type { Session } from '@/classes/Session'
import SubmissionErrorCard from '@/components/home/SubmissionErrorCard.vue'
import SubmissionStatusModal from '@/components/home/SubmissionStatuses/SubmissionStatusModal.vue'
import { usePresentationLocale } from '@/i18n'
import { useDocumentStore } from '@/stores/document'
import { useSessionStore } from '@/stores/session'
import { useSubmissionStore } from '@/stores/submission'
import {
	ChevronDownIcon,
	ExclamationTriangleIcon,
} from '@heroicons/vue/24/solid'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'

type SubmissionErrorGroup = {
	id: string
	firstPlanHolderId: string
	planHolderIds: string[]
	session?: Session
	errorCount: number
}

const documentStore = useDocumentStore()
const sessionStore = useSessionStore()
const submissionStore = useSubmissionStore()
const { t } = usePresentationLocale()

const openStatusModal = ref(false)
const errorsExpanded = ref(true)

const statusPlans = ref<
	{
		id: string
		name: string
	}[]
>([])

const submissionErrorGroups = computed<SubmissionErrorGroup[]>(() => {
	const groups = new Map<string, SubmissionErrorGroup>()

	for (const [planHolderId] of submissionStore.failedSubmissions?.entries() ??
		[]) {
		const errorCount = submissionStore.getSubmissionErrorCount(planHolderId)

		if (errorCount === 0) continue

		const session = sessionStore.sessions.find((session) =>
			session.planIds.some(
				(id) => id.toUpperCase() === planHolderId.toUpperCase()
			)
		)

		const remoteSessionId =
			submissionStore.remoteSubmissionErrors[planHolderId.toUpperCase()]
				?.latestSessionId

		// Local errors use the local session. Remote-only errors use the
		// session returned by the submission errors endpoint.
		const sessionId = session?.id ?? remoteSessionId ?? planHolderId

		const existingGroup = groups.get(sessionId)

		if (existingGroup) {
			existingGroup.errorCount += errorCount

			if (
				!existingGroup.planHolderIds.some(
					(id) => id.toUpperCase() === planHolderId.toUpperCase()
				)
			) {
				existingGroup.planHolderIds.push(planHolderId)
			}

			continue
		}

		const planHolderIds = session?.planIds ?? [planHolderId]

		groups.set(sessionId, {
			id: sessionId,
			firstPlanHolderId: planHolderIds[0] ?? planHolderId,
			session,
			planHolderIds,
			errorCount,
		})
	}

	return [...groups.values()]
})

const openSubmissionStatus = async (group: SubmissionErrorGroup) => {
	const plans = await Promise.all(
		group.planHolderIds.map(async (planHolderId) => {
			const plan = await submissionStore.loadPlanHolderBasicData(planHolderId)

			return {
				id: planHolderId,
				name: `${plan?.firstName ?? ''} ${plan?.lastName ?? ''}`.trim(),
			}
		})
	)

	statusPlans.value = plans

	await nextTick()

	openStatusModal.value = true
}

onMounted(() => {
	// This will make sure documents for all failed submission requests are ready for re-submission.
	for (const planHolderId of submissionStore.planHoldersWithFailures) {
		documentStore.loadDocumentsForPlanHolder([planHolderId])
	}
})

onUnmounted(() => {
	documentStore.setPlanHoldersDocuments([])
})
</script>

<style scoped>
.error-cards-leave-active {
	transition: all 0.5s ease;
}

.error-cards-leave-to {
	opacity: 0;
	transform: translateY(-30px);
}
</style>
